import { createSlice, nanoid, type PayloadAction } from '@reduxjs/toolkit';
import type { ListEntity } from '../api/types';
import { readJson, STORAGE_KEYS } from './storage';

export interface Toast {
  id: string;
  message: string;
}

/** Collapsible parts of the menu: the lists (views), the Data group and its OAA and Stripe tables. */
export type NavSection = ListEntity | 'data' | 'oaa' | 'stripe' | 'bi' | 'offers';

/** Shared UI state: account drawer, expanded menu submenus, hidden chart bands and toasts. */
export interface UiState {
  accountDrawerId: string | null;
  navExpanded: Partial<Record<NavSection, boolean>>;
  /** Chart bands above a list that the viewer folded away, by list; remembered in this browser. */
  chartsHidden: Partial<Record<string, boolean>>;
  toasts: Toast[];
}

const initialState: UiState = {
  accountDrawerId: null,
  navExpanded: {},
  chartsHidden: {},
  toasts: [],
};

export const loadNavExpanded = (): UiState['navExpanded'] => readJson(STORAGE_KEYS.nav, {});
export const loadChartsHidden = (): UiState['chartsHidden'] => readJson(STORAGE_KEYS.charts, {});

export const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    accountOpened(state, action: PayloadAction<string>) {
      state.accountDrawerId = action.payload;
    },
    accountClosed(state) {
      state.accountDrawerId = null;
    },
    navToggled(state, action: PayloadAction<{ section: NavSection; expanded: boolean }>) {
      state.navExpanded[action.payload.section] = action.payload.expanded;
    },
    chartsToggled(state, action: PayloadAction<{ band: string; hidden: boolean }>) {
      state.chartsHidden[action.payload.band] = action.payload.hidden;
    },
    toastShown: {
      reducer(state, action: PayloadAction<Toast>) {
        state.toasts.push(action.payload);
      },
      prepare: (message: string) => ({ payload: { id: nanoid(), message } }),
    },
    toastExpired(state, action: PayloadAction<string>) {
      state.toasts = state.toasts.filter((t) => t.id !== action.payload);
    },
  },
  selectors: {
    selectAccountDrawerId: (state) => state.accountDrawerId,
    selectNavExpanded: (state) => state.navExpanded,
    selectChartsHidden: (state) => state.chartsHidden,
    selectToasts: (state) => state.toasts,
  },
});

export const { accountOpened, accountClosed, navToggled, chartsToggled, toastShown, toastExpired } =
  uiSlice.actions;
export const { selectAccountDrawerId, selectNavExpanded, selectChartsHidden, selectToasts } =
  uiSlice.selectors;
